import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { dsClass } from '../../common/constants/dsClasses.js';
import { eurosPerMonthCapMsg, t } from '../../common/i18n/index.js';
import { formatSumToString } from '../../common/utils/priceUtils.js';
import { clampQuantity } from '../../common/utils/quantityUtils.js';
import { isMobileView } from '../HorizontalSubscriptionCard/SubscriptionUtils.js';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy.js';
import type { ReactNode } from 'react';

import '../HorizontalSubscriptionCard/SubscriptionCard.scss';

interface InfoItemsListProps {
  infoItems: CardInfoItem[];
}

const InfoItemsList = ({ infoItems }: InfoItemsListProps) => (
  <ul>
    {infoItems.map(item => (
      <li key={item.key}>
        <CL.Icon className="of-info-icon" icon={item.icon} size="s" />
        <span className={`of-info-item-text ${dsClass.TEXT_S}`}>{item.text}</span>
      </li>
    ))}
  </ul>
);

export interface CardBadgeItem {
  key: string;
  color: '5g' | 'blue-subtle';
  text: string;
}

export interface CardInfoItem {
  key: string;
  icon: string;
  text: string;
}

interface AllBadgesProps {
  badges: CardBadgeItem[];
}

const AllBadges = ({ badges }: AllBadgesProps) => (
  <>
    {badges.map(badge => (
      <CL.Badge key={badge.key} color={badge.color} text={badge.text} />
    ))}
  </>
);

export interface ProductOfferingCardPresenterCoCoProps {
  badges: CardBadgeItem[];
  infoItems: CardInfoItem[];
  subHeader?: string;
  header: string;
  image?: string;
  description?: string;
  renderAdditionalContent?: () => ReactNode;
  recurringCharge: number;
  quantity: number;
  serviceInfo: ReactNode;
  hasQuantity: boolean;
  buttonText: string;
  onQuantityChange: (quantity: number) => void;
  onOpenModal: () => void;
}

export const ProductOfferingCardPresenterCoCo = ({
  badges,
  infoItems,
  subHeader,
  header,
  image,
  description,
  renderAdditionalContent,
  recurringCharge,
  quantity,
  serviceInfo,
  hasQuantity,
  buttonText,
  onQuantityChange,
  onOpenModal,
}: ProductOfferingCardPresenterCoCoProps) => {
  const hideQuantity = !hasQuantity;
  const marginByBadgesLength = () => {
    switch (badges.length) {
      case 0:
        return '';
      case 1:
        return dsClass.MARGIN_TOP_2;
      default:
        return dsClass.MARGIN_TOP_1;
    }
  };

  return (
    <div className="of-subscription-card-container">
      <div className={`of-subscription-card-content ${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID}`}>
        <div className="of-subscription-card-content-badges">
          <AllBadges badges={badges} />
        </div>
        <div className={`of-subscription-card-content-flex-container ${dsClass.PADDING_BOTTOM_2}`}>
          {image && (
            <div className="of-subscription-card-content-flex-item of-software-product-image">
              <img alt="" src={`${pictureStorageBaseUrl}${image}`} />
            </div>
          )}
          <div className="of-subscription-card-content-flex-item of-main-content">
            <div className="of-subscription-card-content-flex-container">
              <div className="of-subscription-card-content-flex-item of-main-text-content">
                <div className={`of-subscription-card-content-header ${marginByBadgesLength()}`}>
                  <span>{subHeader}</span>
                  <h3 className={`${dsClass.MARGIN_TOP_1} ${dsClass.MARGIN_BOTTOM_2}`}>{header}</h3>
                </div>
                <div className="of-subscription-card-content-description">
                  <span className={dsClass.TEXT_M}>{description}</span>
                </div>
              </div>
              <div className="of-subscription-card-content-flex-item of-main-list-content of-subscription-card-align-content-end">
                <div className="of-subscription-card-content-info-list">
                  <InfoItemsList infoItems={infoItems} />
                </div>
              </div>
            </div>
            {isMobileView && renderAdditionalContent?.()}
          </div>

          <div className="of-subscription-card-content-flex-item of-price-content">
            <div className={`of-subscription-card-content-price ${dsClass.MARGIN_BOTTOM_1}`}>
              <CL.Price
                currentPrice={`${formatSumToString(recurringCharge * quantity, true)}`}
                currentPriceUnit={t.H37G(eurosPerMonthCapMsg)}
                additionalInfo={serviceInfo}
              />
            </div>
            <div
              className={classNames('of-subscription-card-content-button', {
                ['without-quantity']: hideQuantity,
              })}
            >
              {hasQuantity && (
                <CL.Quantity
                  className={`of-subscription-quantity ${dsClass.MARGIN_RIGHT_2}`}
                  minValue={1}
                  currentValue={quantity}
                  onChange={(q: number) => onQuantityChange(clampQuantity(q))}
                />
              )}
              <CL.Button aria-label={buttonText} size="l" block={hideQuantity} onClick={onOpenModal}>
                {buttonText}
              </CL.Button>
            </div>
          </div>
        </div>
        {!isMobileView && renderAdditionalContent?.()}
      </div>
    </div>
  );
};
